import { Navigate, Route, Routes } from 'react-router-dom';
import { NoticeLayout } from './NoticeLayout';
import { NoticeAuthProvider } from './NoticeAuthContext';
import { NoticeHome } from './panels/NoticeHome';
import { NoticeViewer } from './panels/NoticeViewer';
import { NoticeLibrary } from './panels/NoticeLibrary';
import { NoticeCatalog } from './panels/NoticeCatalog';
import { NoticeAdmin } from './panels/NoticeAdmin';
import { PublicTutorial } from './public/PublicTutorial';
import { NoticeSaves } from './panels/NoticeSaves';
import { IS_DEMO, IS_SANDBOX } from '../../apiBase';

// NoticeIA: upload PDFs and read them page by page. NoticeIA has its own
// Google-auth account pool (NoticeAuthProvider), separate from the rest of Lumna.
export function NoticeApp() {
  return (
    <NoticeAuthProvider>
      <Routes>
        {/* THE PUBLIC PAGE (QR target): no login, no app shell. Kept a sibling of the layout
            route and more specific than its "*" child, so it wins. One address,
            `/tutorials/<slug>`: the doc-id one, `/t/<sha>`, no longer answers (Louis 2026-10-26). */}
        <Route path="/tutorials/:slug" element={<PublicTutorial />} />
        <Route path="/" element={<NoticeLayout />}>
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<NoticeHome />} />
          {/* One tab, two pages. /view is the list of manuals; /view/<slug> is one
              of them, open. A static segment outranks a dynamic one in the router,
              so "new" reaches the fresh-upload page and never resolves as an id. */}
          <Route path="view" element={<NoticeLibrary />} />
          <Route path="view/new" element={<NoticeViewer fresh />} />
          <Route path="view/:id" element={<NoticeViewer />} />
          {/* The list used to live here, under its own tab called History. */}
          <Route path="library" element={<Navigate to="/view" replace />} />
          <Route path="catalog" element={<NoticeCatalog />} />
          {/* THE SANDBOX'S OWN PAGE, and only its own: on prod and the demo the routes
              behind it answer `not_here`, so an address typed there would open a page
              that could never list anything. The tab is gone from the rail as well
              (NoticeLayout) — this is what is reachable, that is what is offered. */}
          {IS_SANDBOX && <Route path="saves" element={<NoticeSaves />} />}
          {/* NOT IN THE DEMO, and taken out of the ROUTER rather than out of the
              sidebar: a tab that is merely unlisted is still a URL somebody can
              type, and the "*" route below turns these into /view instead. The
              page itself is the admin console — the
              spending, the accounts, the pull from prod. */}
          {!IS_DEMO && (
            <>
              <Route path="admin" element={<NoticeAdmin />} />
              {/* Pricing, users and analytics folded into the Admin page. */}
              <Route path="pricing" element={<Navigate to="/admin" replace />} />
              <Route path="users" element={<Navigate to="/admin" replace />} />
              <Route path="analytics" element={<Navigate to="/admin" replace />} />
            </>
          )}
          {/* ABSOLUTE, and it has to be. Relative, this resolves against whatever the
              unknown path was — so /database became /database/view, which matches this
              route again, and again: the URL grew a hundred segments long, the app
              navigated in a tight loop and the tab froze. Any deep link to a path this
              router does not know did that. */}
          <Route path="*" element={<Navigate to="/view" replace />} />
        </Route>
      </Routes>
    </NoticeAuthProvider>
  );
}
